import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { SignedImage } from './SignedImage';

interface Option {
  id: string | number;
  label: string;
  image?: string;
  [key: string]: any;
}

interface ComboBoxProps {
  options: Option[];
  value: string | number | null | undefined;
  onChange: (value: string | number | null) => void;
  onSearchChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  className?: string;
  inputClassName?: string;
  showImage?: boolean;
  isLoading?: boolean;
}

export const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  value,
  onChange,
  onSearchChange,
  placeholder = 'Seleziona...',
  disabled = false,
  label,
  className,
  inputClassName,
  showImage = false,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(o => String(o.id) === String(value));
  
  // Quando non è aperto, mostriamo il valore selezionato nell'input
  // Quando è aperto, mostriamo la ricerca
  const displayValue = isOpen ? search : (selectedOption ? selectedOption.label : '');

  const filteredOptions = options.filter(o => 
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      if (onSearchChange) onSearchChange('');
      inputRef.current?.focus();
    }
  }, [isOpen]); // Rimossa dipendenza onSearchChange per evitare reset loop

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (onSearchChange) onSearchChange(val);
  };

  return (
    <div className={cn("relative flex flex-col gap-1", className)} ref={wrapperRef}>
      {label && <label className="text-[10px] uppercase font-bold text-slate-400 mb-0.5 ml-1">{label}</label>}
      
      <div 
        className={cn(
          "relative flex items-center bg-slate-50 border rounded-xl text-sm transition-all shadow-sm",
          inputClassName,
          disabled && "opacity-60 cursor-not-allowed bg-transparent shadow-none",
          isOpen && "ring-2 ring-primary/20 border-primary bg-white shadow-md",
          !disabled && !isOpen && "hover:border-slate-300 hover:bg-slate-100/50 cursor-pointer"
        )}
        onClick={() => {
          if (!disabled && !isOpen) setIsOpen(true);
        }}
      >
        <div className="flex items-center gap-2 pl-3 w-full pr-10 py-2 min-h-[40px]">
          {showImage && selectedOption?.image && !isOpen && (
            <SignedImage path={selectedOption.image} className="w-5 h-4 object-contain flex-shrink-0" />
          )}
          <input
            ref={inputRef}
            type="text"
            readOnly={!isOpen}
            disabled={disabled}
            className={cn(
              "w-full bg-transparent border-none outline-none text-sm font-medium placeholder:text-slate-400 placeholder:font-normal",
              isOpen ? "cursor-text" : "cursor-pointer"
            )}
            placeholder={selectedOption ? selectedOption.label : placeholder}
            value={displayValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => !disabled && setIsOpen(true)}
          />
        </div>
        
        <div className="absolute right-3 flex items-center gap-1">
          {isLoading ? (
             <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          ) : isOpen ? (
            <Search className="w-4 h-4 text-primary animate-pulse" />
          ) : (
            <ChevronDown className={cn("w-4 h-4 text-slate-400 transition-transform", isOpen && "rotate-180")} />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-[100] top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 backdrop-blur-sm bg-white/95">
          <div className="max-h-[300px] overflow-y-auto p-1.5 custom-scrollbar">
            {isLoading && filteredOptions.length === 0 ? (
               <div className="flex flex-col items-center justify-center px-4 py-8 text-slate-400">
                <div className="w-8 h-8 border-2 border-slate-100 border-t-primary rounded-full animate-spin mb-3" />
                <span className="text-xs italic font-medium">Ricerca in corso...</span>
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.id}
                  className={cn(
                    "group flex items-center justify-between px-4 py-2.5 rounded-xl text-sm cursor-pointer transition-all",
                    String(opt.id) === String(value) 
                      ? "bg-primary text-white font-bold shadow-md shadow-primary/20" 
                      : "hover:bg-slate-50 text-slate-600 hover:text-slate-900"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(opt.id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <div className="flex items-center gap-3">
                    {showImage && opt.image && (
                      <SignedImage path={opt.image} className={cn("w-6 h-4 object-contain rounded-sm transition-transform group-hover:scale-110", String(opt.id) === String(value) ? "" : "opacity-80")} />
                    )}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {String(opt.id) === String(value) && <Check className="w-4 h-4" />}
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center px-4 py-8 text-slate-400">
                <Search className="w-8 h-8 opacity-10 mb-2" />
                <span className="text-xs italic font-medium">Nessun risultato trovato</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
